import React, { useCallback, useMemo } from 'react';

import { ReopenImpact, ReopenRoom } from '~/types/reopen';
import { CatalogRoom, ExtractedDateRange } from '~/types/stopsell';

import ReopenRoomGroup from './ReopenRoomGroup';

interface Props {
  roomIndex: number;
  room: ReopenRoom;
  localDates: Array<ExtractedDateRange>;
  impacts: Array<Array<ReopenImpact>>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  deselectedBlockIds: Set<string>;
  onDateChange: (roomIndex: number, dateIndex: number, value: boolean) => void;
  onToggleAll: (roomIndex: number, value: boolean) => void;
  onRoomTypeChange: (roomIndex: number, roomTypeId: string) => void;
  onEditDate: (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => void;
  onDeleteDate: (roomIndex: number, dateId: string) => void;
  onAddExtractedDate: (roomIndex: number, start: string, end: string) => void;
  onBlockToggle: (blockId: string, selected: boolean) => void;
}

export default function ReopenRoomGroupRow({
  roomIndex,
  room,
  localDates,
  impacts,
  catalogRooms,
  selectedRoomTypeId,
  deselectedBlockIds,
  onDateChange,
  onToggleAll,
  onRoomTypeChange,
  onEditDate,
  onDeleteDate,
  onAddExtractedDate,
  onBlockToggle,
}: Props) {
  const selectedCatalogRoom = useMemo(
    () => catalogRooms.find((c) => c.id === selectedRoomTypeId),
    [catalogRooms, selectedRoomTypeId],
  );

  const blackoutDates = useMemo(() => selectedCatalogRoom?.blackoutDates ?? [], [selectedCatalogRoom]);

  const catalogRoomRates = useMemo(() => selectedCatalogRoom?.roomRates ?? [], [selectedCatalogRoom]);

  const handleChange = useCallback(
    (dateIndex: number, value: boolean) => onDateChange(roomIndex, dateIndex, value),
    [roomIndex, onDateChange],
  );

  const handleToggleAll = useCallback((value: boolean) => onToggleAll(roomIndex, value), [roomIndex, onToggleAll]);

  const handleRoomTypeChange = useCallback(
    (roomTypeId: string) => onRoomTypeChange(roomIndex, roomTypeId),
    [roomIndex, onRoomTypeChange],
  );

  const handleEditDate = useCallback(
    (dateId: string, newRange: { start: string; end: string }) => onEditDate(roomIndex, dateId, newRange),
    [roomIndex, onEditDate],
  );

  const handleDeleteDate = useCallback((dateId: string) => onDeleteDate(roomIndex, dateId), [roomIndex, onDeleteDate]);

  const handleAddExtractedDate = useCallback(
    (start: string, end: string) => onAddExtractedDate(roomIndex, start, end),
    [roomIndex, onAddExtractedDate],
  );

  return (
    <ReopenRoomGroup
      room={room}
      localDates={localDates}
      impacts={impacts}
      catalogRooms={catalogRooms}
      selectedRoomTypeId={selectedRoomTypeId}
      blackoutDates={blackoutDates}
      catalogRoomRates={catalogRoomRates}
      deselectedBlockIds={deselectedBlockIds}
      onChange={handleChange}
      onToggleAll={handleToggleAll}
      onRoomTypeChange={handleRoomTypeChange}
      onEditDate={handleEditDate}
      onDeleteDate={handleDeleteDate}
      onAddExtractedDate={handleAddExtractedDate}
      onBlockToggle={onBlockToggle}
    />
  );
}
